<template>
  <DxPopup
    :visible="isVisible"
    :width="600"
    :height="480"
    :title="popupTitle"
    :show-close-button="true"
    :drag-enabled="false"
    @hiding="resetForm"
    class="add-customer-popup"
  >
    <template #content>
      <transition :name="transition" mode="out-in">
        <div :key="currentSlide">
          <!-- Customer slide -->
          <div v-if="currentSlide === CUSTOMER_SLIDE">
            <DxValidationGroup ref="customerSlideValidationGroup">
              <div class="row justify-between">
                <DxTextBox
                  v-model="customer.name"
                  label="Input Name"
                  class="custom-input"
                >
                  <DxValidator :validation-rules="[requiredFieldRule]" />
                </DxTextBox>
                <DxTextBox
                  v-model="customer.emailSuffix"
                  label="Input email suffix"
                  class="custom-input"
                >
                  <DxValidator
                    :validation-rules="[requiredFieldRule, noAtSignRule]"
                  />
                </DxTextBox>
              </div>
              <div class="row justify-between">
                <DxTextBox
                  v-model="customer.crmIdentifier"
                  label="Input CRM identifier"
                  class="custom-input"
                >
                  <DxValidator
                    :validation-rules="[requiredFieldRule, minSixCharsRule]"
                  />
                </DxTextBox>
                <DxSelectBox
                  v-model="customer.customerType"
                  :search-enabled="true"
                  label="Select type"
                  :data-source="customerTypes"
                  class="custom-select"
                >
                  <DxValidator :validation-rules="[requiredFieldRule]" />
                </DxSelectBox>
              </div>
              <div class="row justify-between">
                <DxSelectBox
                  v-model="customer.accountManager.id"
                  :search-enabled="true"
                  label="Select account manager"
                  :data-source="accountManagers"
                  display-expr="fullName"
                  value-expr="id"
                  class="custom-select"
                >
                  <DxValidator :validation-rules="[requiredFieldRule]" />
                </DxSelectBox>
                <DxTextBox
                  v-model="customer.debugEmail"
                  label="Debug email"
                  mode="email"
                  class="custom-input"
                />
              </div>
              <div
                :class="
                  hasSuperAdminRole
                    ? 'row justify-between'
                    : 'row justify-around'
                "
              >
                <DxTagBox
                  v-model="customer.selectedProducts"
                  :items="products"
                  :search-enabled="true"
                  label="Select product(s)"
                  class="custom-tagbox"
                >
                  <DxValidator :validation-rules="[requiredFieldRule]" />
                </DxTagBox>
                <DxSelectBox
                  v-if="hasSuperAdminRole"
                  v-model="customer.languageCode"
                  :search-enabled="true"
                  label="Language code"
                  :data-source="languageCodes"
                  display-expr="description"
                  value-expr="id"
                  class="custom-select"
                >
                  <DxValidator :validation-rules="[requiredFieldRule]" />
                </DxSelectBox>
              </div>
            </DxValidationGroup>
          </div>
          <!-- Content slide -->
          <div v-if="currentSlide === CONTENT_SLIDE">
            <DxValidationGroup ref="contentSlideValidationGroup">
              <div class="row justify-between">
                <DxDateBox
                  v-model="customer.content.startDate"
                  type="date"
                  display-format="dd-MM-yyyy"
                  label="Start date"
                  class="custom-date"
                >
                  <DxValidator :validation-rules="[requiredFieldRule]" />
                </DxDateBox>
                <DxDateBox
                  v-model="customer.content.endDate"
                  type="date"
                  display-format="dd-MM-yyyy"
                  label="End date"
                  class="custom-date"
                />
              </div>
              <div class="row justify-between">
                <DxNumberBox
                  v-model="customer.content.numberOfLicences"
                  :min="0"
                  :step="1"
                  :show-spin-buttons="true"
                  label="Input number of licences"
                  label-mode="floating"
                  class="custom-input"
                >
                  <DxValidator
                    :validation-rules="[
                      requiredFieldRule,
                      greaterThanZeroRule,
                      integerOnlyRule
                    ]"
                  />
                </DxNumberBox>
                <DxCheckBox
                  v-model="customer.content.isDemo"
                  text="Demo"
                  class="custom-checkbox"
                />
              </div>
            </DxValidationGroup>
          </div>
          <!-- SAS slide -->
          <div v-if="currentSlide === SAS_SLIDE">
            <DxValidationGroup ref="sasSlideValidationGroup">
              <div class="row justify-between">
                <DxDateBox
                  v-model="customer.sas.sasStartDate"
                  type="date"
                  display-format="dd-MM-yyyy"
                  label="Start date"
                  class="custom-date"
                />
                <DxDateBox
                  v-model="customer.sas.sasEndDate"
                  type="date"
                  display-format="dd-MM-yyyy"
                  label="End date"
                  class="custom-date"
                />
              </div>
              <div class="row justify-between">
                <DxSelectBox
                  v-model="customer.sas.ssoOr2Fa"
                  :items="ssoOptions"
                  :search-enabled="true"
                  label="SSO/2FA"
                  class="custom-select"
                >
                  <DxValidator :validation-rules="[requiredFieldRule]" />
                </DxSelectBox>
                <DxTextBox
                  v-if="customer.sas.ssoOr2Fa == 'SSO'"
                  v-model="customer.tenantId"
                  label="Tenant ID"
                  class="custom-input"
                >
                  <DxValidator :validation-rules="[guidRule]" />
                </DxTextBox>
              </div>
              <div class="row justify-between">
                <DxSelectBox
                  v-model="customer.sas.reminderInterval"
                  :data-source="reminderIntervalsList"
                  display-expr="description"
                  value-expr="id"
                  :search-enabled="true"
                  label="Select reminder interval"
                  class="custom-select"
                />
                <DxCheckBox
                  v-model="customer.sas.canAllCompleteTask"
                  text="Project user can set task to finished"
                  class="custom-checkbox"
                />
              </div>
              <div class="row justify-between">
                <DxTextBox
                  v-model="customer.sas.emailReplyAddress"
                  label="Email reply address"
                  class="custom-input"
                >
                  <DxValidator
                    :validation-rules="[requiredFieldRule, emailRule]"
                  />
                </DxTextBox>
                <DxTextBox
                  v-model="customer.sas.emailSignature"
                  label="Email signature"
                  class="custom-input"
                />
              </div>
              <div class="row justify-between">
                <DxTextBox
                  v-model="customer.sas.categorization1"
                  label="Categorization 1"
                  class="custom-input"
                />
                <DxTextBox
                  v-model="customer.sas.categorization2"
                  label="Categorization 2"
                  class="custom-input"
                />
              </div>
              <div class="row justify-between">
                <DxCheckBox
                  v-model="customer.sas.useCustomEmailAddress"
                  text="Use custom email address"
                  class="custom-checkbox"
                />
                <DxTextBox
                  v-if="customer.sas.useCustomEmailAddress"
                  v-model="customer.sas.emailsSendFrom"
                  label="Email sending address"
                  class="custom-input"
                >
                  <DxValidator
                    :validation-rules="[requiredFieldRule, emailRule]"
                  />
                </DxTextBox>
              </div>
            </DxValidationGroup>
          </div>
        </div>
      </transition>
      <!-- Navigation -->
      <div class="popup-navigation">
        <DxButton
          text="Back"
          :disabled="currentSlide === CUSTOMER_SLIDE"
          @click="handlePreviousStep"
          class="orange-btn custom-btn"
        />
        <DxButton
          :text="nextOrSubmitBtn"
          type="success"
          @click="handleNextStep"
          class="orange-btn-filled custom-btn"
        />
      </div>
    </template>
  </DxPopup>
</template>

<script setup>
import { ref, defineProps, computed, onMounted } from "vue";
import DxPopup from "devextreme-vue/popup";
import DxButton from "devextreme-vue/button";
import DxTextBox from "devextreme-vue/text-box";
import DxSelectBox from "devextreme-vue/select-box";
import DxDateBox from "devextreme-vue/date-box";
import DxNumberBox from "devextreme-vue/number-box";
import DxCheckBox from "devextreme-vue/check-box";
import DxValidator from "devextreme-vue/validator";
import DxValidationGroup from "devextreme-vue/validation-group";
import DxTagBox from "devextreme-vue/tag-box";
import store from "../../store/index.js";
import CustomersService from "../../services/customers.service.js";
import AccountManagersService from "../../services/account.managers.service.js";

const props = defineProps({
  showAdd: Boolean
});

onMounted(async () => {
  await CustomersService.getLanguageCodes();
  await CustomersService.getCustomerTypesNew();
  await AccountManagersService.getAccountManagersWithBasicInfo();
  await CustomersService.getDeliveryMethods();
  await CustomersService.getReminderIntervals();
});

const CUSTOMER_SLIDE = 0;
const CONTENT_SLIDE = 1;
const SAS_SLIDE = 2;
const CONTENT_PRODUCT = "Content";
const SAS_PRODUCT = "SAS";
const BTN_NEXT = "Next";
const BTN_SUBMIT = "Submit";
const TRANSITION_FORWARD = "forward";
const TRANSITION_BACKWARD = "backward";

const requiredFieldRule = {
  type: "required",
  message: "Field is required"
};
const noAtSignRule = {
  type: "custom",
  validationCallback: e => {
    const val = e.value || "";
    return val.length > 0 && !/@/.test(val);
  },
  message: "Please fill without @"
};
const minSixCharsRule = {
  type: "custom",
  validationCallback: e => {
    return e.value?.length > 5;
  },
  message: "Minimum 6 characters needed"
};
const greaterThanZeroRule = {
  type: "custom",
  validationCallback: e => {
    const val = Number(e.value);
    return !isNaN(val) && val > 0;
  },
  message: "Value must be greater than 0"
};
const integerOnlyRule = {
  type: "custom",
  validationCallback: e => Number.isInteger(e.value),
  message: "Only whole numbers allowed"
};
const emailRule = {
  type: "email",
  message: "Please enter a valid email address"
};
const guidRule = {
  type: "pattern",
  pattern: /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/,
  message:
    "Please enter a valid GUID (e.g. 3f2504e0-4f89-11d3-9a0c-0305e82c3301)"
};

const isVisible = ref(props.showAdd);
const currentSlide = ref(0);
const popupTitle = ref("Add new customer");
const products = ref([CONTENT_PRODUCT, SAS_PRODUCT]);
const ssoOptions = ref(["SSO", "2FA"]);
const direction = ref(TRANSITION_FORWARD);
const customerSlideValidationGroup = ref(null);
const contentSlideValidationGroup = ref(null);
const sasSlideValidationGroup = ref(null);
const nextOrSubmitBtn = ref(BTN_NEXT);

const customer = computed(() => store.state.customers.createCustomerObject);
const languageCodes = computed(() => store.state.customers.languageCodes);
const customerTypes = computed(() => store.state.customers.customerTypesList);
const hasSuperAdminRole = computed(
  () => store.state.auth.authUser.isSuperAdmin
);
const accountManagers = computed(
  () => store.state.accountManagers.accountManagersBasicInfoList
);
const reminderIntervalsList = computed(
  () => store.state.customers.reminderIntervalOptions
);
const transition = computed(() =>
  direction.value === "forward" ? "slide-right" : "slide-left"
);

const handleNextStep = () => {
  const products = customer.value.selectedProducts;
  switch (currentSlide.value) {
    case CUSTOMER_SLIDE:
      if (!isSlideValid()) break;
      if (products.includes(CONTENT_PRODUCT))
        moveToSlide(
          CONTENT_SLIDE,
          TRANSITION_FORWARD,
          products.includes(SAS_PRODUCT) ? BTN_NEXT : BTN_SUBMIT
        );
      else if (products.includes(SAS_PRODUCT))
        moveToSlide(SAS_SLIDE, TRANSITION_FORWARD, BTN_SUBMIT);
      break;
    case CONTENT_SLIDE:
      if (!isSlideValid()) break;
      if (products.includes(SAS_PRODUCT))
        moveToSlide(SAS_SLIDE, TRANSITION_FORWARD, BTN_SUBMIT);
      else submitForm();
      break;
    case SAS_SLIDE:
      if (isSlideValid()) submitForm();
      break;
  }
};
const handlePreviousStep = () => {
  const products = customer.value.selectedProducts;
  switch (currentSlide.value) {
    case CONTENT_SLIDE:
      moveToSlide(CUSTOMER_SLIDE, TRANSITION_BACKWARD, BTN_NEXT);
      break;
    case SAS_SLIDE:
      if (products.includes(CONTENT_PRODUCT))
        moveToSlide(CONTENT_SLIDE, TRANSITION_BACKWARD, BTN_NEXT);
      else moveToSlide(CUSTOMER_SLIDE, TRANSITION_BACKWARD, BTN_NEXT);
      break;
  }
};
const moveToSlide = (slideNumber, directionValue, buttonName) => {
  direction.value = directionValue;
  currentSlide.value = slideNumber;
  popupTitle.value = updatePopupTitle(currentSlide.value);
  nextOrSubmitBtn.value = buttonName;
};
const isSlideValid = () => {
  switch (currentSlide.value) {
    case CUSTOMER_SLIDE:
      return customerSlideValidationGroup.value?.instance?.validate()?.isValid;
    case CONTENT_SLIDE:
      return contentSlideValidationGroup.value?.instance?.validate()?.isValid;
    case SAS_SLIDE:
      return sasSlideValidationGroup.value?.instance?.validate()?.isValid;
  }
  return false;
};
const updatePopupTitle = step => {
  switch (step) {
    case 1:
      return "Add new customer (Content)";
    case 2:
      return "Add new customer (SAS)";
    default:
      return "Add new customer";
  }
};
const submitForm = async () => {
  await CustomersService.createNewCustomer(customer.value);
  hideModal();
};
const resetForm = () => {
  currentSlide.value = CUSTOMER_SLIDE;
  hideModal();
};
const hideModal = () => {
  store.commit("customers/resetCreateCustomerObject");
  store.commit("customers/disableAdd");
};
</script>
<style>
:deep(.dx-editor-cell .dx-texteditor .dx-texteditor-input) {
  text-align: left !important;
  padding-left: 0 !important;
}
</style>
